import { useState, useEffect, useRef } from "react";
import { User } from "../types/user";
import { getImage, addImage } from "../utils/dexieImageCache";

// The backend doesn't expose a stable key for federated photos and signed-URL
// uploads change URL on every render. We cache the resolved blob in IDB under
// a per-user key, and let the upload/remove handlers explicitly clear it when
// the source changes (see PersonalInformation handlers).
const resolveCacheKey = (user: User | null): string | undefined => {
    if (user?._id && (user.profileImage || user.photoURL)) {
        return `profileImage-${user._id}`;
    }
    return undefined;
};

const useProfileImage = (user: User | null): string | null => {
    const [profileImageURL, setProfileImageURL] = useState<string | null>(null);
    const previousProfileImageURL = useRef<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let objectURL: string | null = null;

        const cacheKey = resolveCacheKey(user);
        const fallbackUrl = user?.profileImage || user?.photoURL || null;

        const loadProfileImage = async () => {
            if (!cacheKey) {
                setProfileImageURL(fallbackUrl);
                return;
            }
            try {
                const blob = await getImage(cacheKey);
                if (blob) {
                    objectURL = URL.createObjectURL(blob);
                    if (isMounted) setProfileImageURL(objectURL);
                    return;
                }
                if (!fallbackUrl) {
                    setProfileImageURL(null);
                    return;
                }
                const response = await fetch(fallbackUrl, { cache: "no-cache" });
                if (response.ok) {
                    const fetchedBlob = await response.blob();
                    await addImage(cacheKey, fetchedBlob);
                    objectURL = URL.createObjectURL(fetchedBlob);
                    if (isMounted) setProfileImageURL(objectURL);
                } else if (response.status === 429) {
                    console.error("Rate limited while fetching profile image.");
                    setProfileImageURL(fallbackUrl);
                } else {
                    console.error("Failed to fetch image:", response.statusText);
                    setProfileImageURL(fallbackUrl);
                }
            } catch (error) {
                console.error("Image caching failed:", error);
                setProfileImageURL(fallbackUrl);
            }
        };

        loadProfileImage();

        return () => {
            isMounted = false;
            if (
                previousProfileImageURL.current &&
                previousProfileImageURL.current.startsWith("blob:")
            ) {
                URL.revokeObjectURL(previousProfileImageURL.current);
            }
            previousProfileImageURL.current = objectURL;
        };
    }, [user]);

    return profileImageURL;
};

export default useProfileImage;
